import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, X, Key, CheckCircle2, ShieldAlert, Phone, ArrowLeft } from 'lucide-react';
import { authService } from '../services/api';
import { SubdomainLink } from '../components/SubdomainLink';
import { isCrossSubdomain, getSubdomainUrl } from '../utils/subdomain';
import { useAuth } from '../hooks/useAuth';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isAdmin } = useAuth();

  const handleBackClick = () => {
    const justLoggedOut = sessionStorage.getItem('just_logged_out') === 'true';
    if (justLoggedOut) {
      sessionStorage.removeItem('just_logged_out');
      navigate('/listing');
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get('redirect');

    const isAuthPath = (path) => {
      if (!path) return false;
      const lowerPath = path.toLowerCase();
      return lowerPath.startsWith('/admin') || lowerPath.startsWith('/my-account');
    };

    if (redirectParam) {
      const target = decodeURIComponent(redirectParam);
      if (isAuthPath(target)) {
        navigate('/listing');
      } else {
        navigate(target);
      }
      return;
    }

    if (location.state?.from) {
      const fromPath = typeof location.state.from === 'string'
        ? location.state.from
        : location.state.from.pathname || '';
      
      if (isAuthPath(fromPath)) {
        navigate('/listing');
        return;
      }
    }

    navigate(-1);
  };


  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  // --- Validation Rules ---
  const validate = (data) => {
    const errors = {};

    if (!data.username.trim()) {
      errors.username = 'Email / Mobile Number is required.';
    }

    if (!data.password) {
      errors.password = 'Password is required.';
    }

    return errors;
  };

  const fieldErrors = validate(formData);

  // Helper: returns classes for an input based on validation state
  const inputClass = (fieldName) => {
    const hasError = isSubmitted && fieldErrors[fieldName];
    if (hasError) {
      return 'block w-full pl-10 pr-3.5 py-3 border-2 border-red-400 rounded-xl bg-white focus:ring-2 focus:ring-red-300 focus:outline-none text-red-700 text-sm shadow-2xs';
    }
    return 'block w-full pl-10 pr-3.5 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#1A734E]/20 focus:border-[#1A734E] focus:outline-none text-gray-800 text-sm shadow-2xs transition-all';
  };

  const passwordInputClass = () => {
    const hasError = isSubmitted && fieldErrors.password;
    if (hasError) {
      return 'block w-full pl-10 pr-12 py-3 border-2 border-red-400 rounded-xl bg-white focus:ring-2 focus:ring-red-300 focus:outline-none text-red-700 text-sm shadow-2xs';
    }
    return 'block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#1A734E]/20 focus:border-[#1A734E] focus:outline-none text-gray-800 text-sm shadow-2xs transition-all';
  };

  const iconClass = (fieldName) => {
    const hasError = isSubmitted && fieldErrors[fieldName];
    if (hasError) return 'absolute inset-y-0 left-0 pl-3.5 flex items-center text-red-400';
    return 'absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400';
  };

  // Forgot Password States
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = mobile, 2 = verify OTP, 3 = set password, 4 = success
  const [forgotMobile, setForgotMobile] = useState('');
  const [retrievedName, setRetrievedName] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotPassword, setForgotPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [showForgotPwd, setShowForgotPwd] = useState(false);
  const [showForgotConfirmPwd, setShowForgotConfirmPwd] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotSubmittedStep1, setForgotSubmittedStep1] = useState(false);
  const [forgotSubmittedStep2, setForgotSubmittedStep2] = useState(false);
  const [forgotSubmittedStep3, setForgotSubmittedStep3] = useState(false);

  // Mask mobile helper
  const maskedMobile = forgotMobile ? forgotMobile.slice(0, 6) + 'XXXX' : '';

  // Validation Helper Rules matching Register.js (Sign-Up)
  const getForgotStep1Errors = () => {
    const errors = {};
    const cleanMobile = (forgotMobile || '').trim();
    if (!cleanMobile) {
      errors.mobile = 'Mobile number is required.';
    } else if (cleanMobile.length !== 10 || !/^\d{10}$/.test(cleanMobile)) {
      errors.mobile = 'Mobile number must be exactly 10 digits.';
    }
    return errors;
  };

  const getForgotStep2Errors = () => {
    const errors = {};
    const cleanOtp = (forgotOtp || '').trim();
    if (!cleanOtp) {
      errors.otp = 'OTP code is required.';
    } else if (cleanOtp.length !== 6 || !/^\d{6}$/.test(cleanOtp)) {
      errors.otp = 'OTP code must be exactly 6 digits.';
    }
    return errors;
  };

  const getForgotStep3Errors = () => {
    const errors = {};
    if (!forgotPassword) {
      errors.password = 'Password is required.';
    } else if (forgotPassword.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }

    if (!forgotConfirmPassword) {
      errors.confirmPassword = 'Confirm password is required.';
    } else if (forgotConfirmPassword !== forgotPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    return errors;
  };

  const step1Errors = getForgotStep1Errors();
  const step2Errors = getForgotStep2Errors();
  const step3Errors = getForgotStep3Errors();

  const getForgotInputClass = (hasError) => {
    if (hasError) {
      return 'block w-full pl-10 pr-3.5 py-3 border-2 border-rose-400 rounded-xl bg-white focus:ring-2 focus:ring-rose-300 focus:outline-none text-rose-800 text-sm shadow-2xs transition-all';
    }
    return 'block w-full pl-10 pr-3.5 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#1A734E]/20 focus:border-[#1A734E] text-gray-800 text-sm shadow-2xs transition-all';
  };

  const getForgotPwdInputClass = (hasError) => {
    if (hasError) {
      return 'block w-full pl-10 pr-12 py-3 border-2 border-rose-400 rounded-xl bg-white focus:ring-2 focus:ring-rose-300 focus:outline-none text-rose-800 text-sm shadow-2xs transition-all';
    }
    return 'block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#1A734E]/20 focus:border-[#1A734E] text-gray-800 text-sm shadow-2xs transition-all';
  };

  // Step 1: Send OTP to Mobile
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setForgotSubmittedStep1(true);
    setForgotError('');
    setForgotSuccess('');

    const errors = getForgotStep1Errors();
    if (Object.keys(errors).length > 0) {
      setForgotError(Object.values(errors)[0]);
      return;
    }

    const cleanMobile = forgotMobile.trim();
    setForgotLoading(true);
    try {
      const res = await authService.forgotPasswordRequest(cleanMobile);
      if (res.success) {
        if (res.data?.name) {
          setRetrievedName(res.data.name);
        }
        setForgotSuccess(res.data?.message || 'OTP has been sent to your registered mobile number');
        setForgotStep(2);
        setForgotSubmittedStep2(false);
      } else {
        setForgotError(res.error || res.data?.detail || res.data?.message || 'Failed to send OTP');
      }
    } catch (err) {
      setForgotError(err.message || 'Error sending OTP');
    } finally {
      setForgotLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setForgotSubmittedStep2(true);
    setForgotError('');

    const errors = getForgotStep2Errors();
    if (Object.keys(errors).length > 0) {
      setForgotError(Object.values(errors)[0]);
      return;
    }

    const cleanOtp = (forgotOtp || '').trim();
    setForgotLoading(true);
    try {
      // Verify OTP against backend database right at Step 2
      const res = await authService.forgotPasswordCheckOtp(forgotMobile.trim(), cleanOtp);
      if (res.success) {
        setForgotStep(3);
        setForgotSubmittedStep3(false);
      } else {
        setForgotError(res.error || res.data?.detail || res.data?.message || 'Invalid OTP code entered.');
      }
    } catch (err) {
      setForgotError(err.message || 'Error verifying OTP');
    } finally {
      setForgotLoading(false);
    }
  };

  // Step 2 Action: Resend OTP
  const handleResendOtp = async () => {
    setForgotError('');
    setForgotSuccess('');
    setForgotLoading(true);
    try {
      const res = await authService.forgotPasswordRequest(forgotMobile.trim());
      if (res.success) {
        setForgotSuccess('OTP has been resent to your registered mobile number');
      } else {
        setForgotError(res.error || res.data?.detail || 'Failed to resend OTP');
      }
    } catch (err) {
      setForgotError(err.message || 'Error resending OTP');
    } finally {
      setForgotLoading(false);
    }
  };

  // Step 3: Enter New Password & Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotSubmittedStep3(true);
    setForgotError('');

    const errors = getForgotStep3Errors();
    if (Object.keys(errors).length > 0) {
      setForgotError(Object.values(errors)[0]);
      return;
    }

    const cleanOtp = forgotOtp.trim();
    setForgotLoading(true);
    try {
      const res = await authService.forgotPasswordVerify(forgotMobile.trim(), cleanOtp, forgotPassword);
      if (res.success) {
        setForgotSuccess(res.data?.message || 'Your password has been reset successfully.');
        setForgotStep(4);
        setTimeout(() => {
          setShowForgotModal(false);
        }, 2500);
      } else {
        setForgotError(res.error || res.data?.detail || res.data?.message || 'Password reset failed');
        if (res.error?.toLowerCase().includes('otp') || res.data?.detail?.toLowerCase().includes('otp')) {
          setForgotStep(2);
        }
      }
    } catch (err) {
      setForgotError(err.message || 'Error resetting password');
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot Password OTP 6-Digit Box handlers
  const handleForgotOtpBoxChange = (val, index) => {
    const cleanDigit = val.replace(/[^0-9]/g, '').slice(-1);
    const otpArray = (forgotOtp || '').padEnd(6, ' ').split('');
    otpArray[index] = cleanDigit || ' ';
    const newOtp = otpArray.join('').trimEnd();
    setForgotOtp(newOtp);

    // Auto-advance focus to next input if digit entered
    if (cleanDigit && index < 5) {
      const nextInput = document.getElementById(`forgot-otp-box-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleForgotOtpBoxKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const currentDigit = (forgotOtp || '')[index];
      if (!currentDigit || currentDigit === ' ') {
        if (index > 0) {
          const prevInput = document.getElementById(`forgot-otp-box-${index - 1}`);
          if (prevInput) prevInput.focus();
        }
      }
    }
  };

  const handleForgotOtpBoxPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      setForgotOtp(pasted);
      const nextIdx = Math.min(pasted.length, 5);
      const targetInput = document.getElementById(`forgot-otp-box-${nextIdx}`);
      if (targetInput) targetInput.focus();
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trigger validation visibility on submit attempt
    setIsSubmitted(true);

    // Block submit if any field-level errors
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    setError('');
    
    const result = await authService.login(formData);
    
    if (result.success) {
      let isAdmin = false;
      try {
        const token = result.data.accessToken;
        if (token) {
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            const roles = payload.roles ? payload.roles.split(',').map(r => r.trim()) : [];
            isAdmin = roles.some(r => [
              'ROLE_SUPER_ADMIN',
              'ROLE_SALES_ADMIN',
              'ROLE_LISTING_ADMIN',
              'ROLE_SUPPORT_ADMIN',
              'ROLE_ADMIN',
              'ROLE_MAGAZINE_SALES'
            ].includes(r));
          }
        }
      } catch (err) {
        console.error("Error decoding token for admin redirect:", err);
      }

      const searchParams = new URLSearchParams(location.search);
      const redirectParam = searchParams.get('redirect');
      
      const rawFrom = redirectParam 
        ? decodeURIComponent(redirectParam) 
        : (location.state?.from || (isAdmin ? '/admin' : '/my-account/manage-listing'));

      let fromPath = '/my-account/manage-listing';
      if (typeof rawFrom === 'string') {
        fromPath = rawFrom;
      } else if (rawFrom && rawFrom.pathname) {
        fromPath = rawFrom.pathname + (rawFrom.search || '') + (rawFrom.hash || '');
      }

      if (isCrossSubdomain(fromPath)) {
        window.location.replace(getSubdomainUrl(fromPath));
      } else {
        navigate(fromPath);
      }
    } else {
      setError(String(result.error));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex font-sans bg-[#F9FAF7]">
      {/* Left Side - Green Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1A734E] text-white p-20 flex-col justify-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#23855B] rounded-full -mr-48 -mt-48 opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#23855B] rounded-full -ml-40 -mb-40 opacity-50"></div>
        
        <div className="relative z-10 max-w-md">
          <h1 className="text-5xl font-bold mb-8">Welcome Back</h1>
          <p className="text-xl leading-relaxed opacity-90 mb-16">
            Log in to manage your vendor profile, view your network, and connect with India's top architects.
          </p>
          
          <div className="flex space-x-16">
            <div>
              <p className="text-4xl font-bold mb-2">2,500+</p>
              <p className="text-sm uppercase tracking-widest opacity-70">Vendors</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">50+</p>
              <p className="text-sm uppercase tracking-widest opacity-70">Cities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 bg-[#F9FAF7] flex items-center justify-center p-4 sm:p-8 min-h-screen overflow-y-auto">
        <div className="max-w-md w-full py-6 sm:py-10">
          <div className="mb-4 sm:mb-6 text-left">
            <button
              type="button"
              onClick={handleBackClick}
              className="inline-flex items-center text-sm font-bold text-[#1A734E] hover:text-[#155D3F] transition-colors group bg-transparent border-0 p-0 cursor-pointer"
            >
              <ArrowLeft size={16} className="mr-1.5 transform group-hover:-translate-x-0.5 transition-transform" />
              Back
            </button>
          </div>
          <div className="text-left mb-6 sm:mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 tracking-tight">Login</h2>
            <p className="text-gray-500 text-sm sm:text-base">Welcome back! Please enter your details.</p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl shadow-2xs">
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            )}
            
            <div className="space-y-4">
              {/* Email / Mobile Number */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 flex items-center justify-between">
                  <span>Email / Mobile Number <span className="text-red-500 ml-0.5">*</span></span>
                </label>
                <div className="relative">
                  <span className={iconClass('username')}>
                    <Mail size={18} />
                  </span>
                  <input
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                    className={inputClass('username')}
                    placeholder="Enter email or mobile number"
                  />
                </div>
                {isSubmitted && fieldErrors.username && (
                  <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.username}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 flex items-center justify-between">
                  <span>Password <span className="text-red-500 ml-0.5">*</span></span>
                </label>
                <div className="relative">
                  <span className={iconClass('password')}>
                    <Lock size={18} />
                  </span>
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    className={passwordInputClass()}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {isSubmitted && fieldErrors.password && (
                  <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.password}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-me"
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer accent-[#1A734E]"
                />
                <label htmlFor="remember-me" className="text-sm text-gray-600 cursor-pointer select-none">
                  Remember me
                </label>
              </div>
              <div className="text-sm">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowForgotModal(true);
                    setForgotStep(1);
                    setForgotError('');
                    setForgotSuccess('');
                    setForgotMobile('');
                    setRetrievedName('');
                    setForgotOtp('');
                    setForgotPassword('');
                    setForgotConfirmPassword('');
                    setForgotSubmittedStep1(false);
                    setForgotSubmittedStep2(false);
                    setForgotSubmittedStep3(false);
                  }}
                  className="text-[#1A734E] font-bold hover:underline cursor-pointer"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3.5 px-4 rounded-xl shadow-md text-base sm:text-lg font-bold text-white bg-[#1A734E] hover:bg-[#155D3F] active:scale-[0.99] focus:outline-none transition-all mt-4 disabled:opacity-70 cursor-pointer"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </div>

            <div className="text-center pt-4">
              <p className="text-gray-500 text-sm sm:text-base">
                Don't have an account? <Link to="/sign-up" state={location.state} className="text-[#1A734E] font-bold hover:underline">Create Account</Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto transition-all duration-300">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-auto border border-gray-100 flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex items-start justify-between p-5 sm:p-6 border-b border-gray-100 bg-gray-50/50 shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Forgot Password</h3>
                  {forgotStep <= 3 && (
                    <span className="px-2.5 py-0.5 rounded-full bg-emerald-100/80 text-[#1A734E] text-[11px] font-bold">
                      Step {forgotStep} of 3
                    </span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">
                  {forgotStep === 1 && "Enter your registered 10-digit mobile number to receive an OTP."}
                  {forgotStep === 2 && `We've sent a 6-digit code to registered mobile: ${maskedMobile}.`}
                  {forgotStep === 3 && "Create a new strong password for your account."}
                  {forgotStep === 4 && "Password reset complete!"}
                </p>
              </div>
              <button
                onClick={() => setShowForgotModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-200/60 transition-colors cursor-pointer shrink-0 -mr-1"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content - Scrollable if height exceeds screen */}
            <div className="p-5 sm:p-6 overflow-y-auto space-y-4">
              {/* Error Alert Badge (Only shown when error exists) */}
              {forgotError && (
                <div className="bg-rose-50 text-rose-800 border border-rose-100 p-3.5 rounded-xl text-xs sm:text-sm flex items-center gap-2.5 font-semibold shadow-2xs">
                  <ShieldAlert size={18} className="text-rose-500 flex-shrink-0" />
                  <span>{forgotError}</span>
                </div>
              )}

              {/* STEP 1: Enter Mobile Number */}
              {forgotStep === 1 && (
                <form onSubmit={handleRequestOtp} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Registered Mobile Number <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <span className={iconClass(forgotSubmittedStep1 && step1Errors.mobile ? 'has-error' : '')}>
                        <Phone size={18} />
                      </span>
                      <input
                        type="text"
                        maxLength={10}
                        required
                        value={forgotMobile}
                        onChange={(e) => setForgotMobile(e.target.value.replace(/[^0-9]/g, ''))}
                        className={getForgotInputClass(forgotSubmittedStep1 && step1Errors.mobile)}
                        placeholder="Enter 10-digit mobile number"
                      />
                    </div>
                    {forgotSubmittedStep1 && step1Errors.mobile && (
                      <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                        {step1Errors.mobile}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowForgotModal(false)}
                      className="w-1/2 py-3 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-1/2 py-3 bg-[#1A734E] hover:bg-[#155D3F] text-white rounded-xl text-sm font-bold active:scale-[0.98] transition-all shadow-md shadow-[#1A734E]/20 flex items-center justify-center cursor-pointer disabled:opacity-50"
                    >
                      {forgotLoading ? "Sending OTP..." : "Send OTP"}
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 2: Enter & Verify OTP */}
              {forgotStep === 2 && (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  {/* Clean Simple Text Display without background box or heavy colors */}
                  <div className="space-y-1 my-1">
                    {retrievedName && (
                      <p className="text-sm font-bold text-gray-900">
                        Hi, {retrievedName}!
                      </p>
                    )}
                    {forgotSuccess && (
                      <p className="text-xs sm:text-sm text-gray-600 leading-snug">
                        {forgotSuccess}
                      </p>
                    )}
                  </div>

                  {/* OTP Code Input */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center justify-between">
                      <span>6-Digit OTP Code <span className="text-rose-500">*</span></span>
                    </label>
                    <div className="flex items-center justify-between gap-1.5 my-1">
                      {[0, 1, 2, 3, 4, 5].map((index) => {
                        const digit = (forgotOtp || '')[index] && (forgotOtp || '')[index] !== ' ' ? (forgotOtp || '')[index] : '';
                        return (
                          <input
                            key={index}
                            id={`forgot-otp-box-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleForgotOtpBoxChange(e.target.value, index)}
                            onKeyDown={(e) => handleForgotOtpBoxKeyDown(e, index)}
                            onPaste={handleForgotOtpBoxPaste}
                            className={`flex-1 min-w-0 h-11 sm:h-13 text-center text-lg sm:text-xl font-bold font-mono rounded-xl border transition-all ${
                              forgotSubmittedStep2 && step2Errors.otp
                                ? 'border-2 border-rose-400 bg-rose-50/30 text-rose-700'
                                : digit
                                ? 'border-[#1A734E] bg-emerald-50/40 text-[#1A734E]'
                                : 'border-gray-200 bg-gray-50/50 text-gray-800'
                            } focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1A734E]/20 focus:border-[#1A734E] shadow-2xs`}
                          />
                        );
                      })}
                    </div>
                    {forgotSubmittedStep2 && step2Errors.otp && (
                      <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                        {step2Errors.otp}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-1 text-xs">
                    <span className="text-gray-500">Didn't receive the code?</span>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={forgotLoading}
                      className="text-[#1A734E] font-bold hover:underline cursor-pointer disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setForgotStep(1);
                        setForgotError('');
                        setForgotSuccess('');
                        setForgotSubmittedStep1(false);
                      }}
                      className="w-1/3 py-3 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-2/3 py-3 bg-[#1A734E] hover:bg-[#155D3F] text-white rounded-xl text-sm font-bold active:scale-[0.98] transition-all shadow-md shadow-[#1A734E]/20 flex items-center justify-center cursor-pointer disabled:opacity-50"
                    >
                      {forgotLoading ? "Verifying..." : "Verify OTP"}
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 3: Enter New Password & Reset */}
              {forgotStep === 3 && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {/* New Password */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      New Password <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400">
                        <Lock size={18} />
                      </span>
                      <input
                        type={showForgotPwd ? "text" : "password"}
                        required
                        value={forgotPassword}
                        onChange={(e) => setForgotPassword(e.target.value)}
                        className={getForgotPwdInputClass(forgotSubmittedStep3 && step3Errors.password)}
                        placeholder="Minimum 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotPwd(!showForgotPwd)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        {showForgotPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {forgotSubmittedStep3 && step3Errors.password ? (
                      <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                        {step3Errors.password}
                      </p>
                    ) : forgotPassword ? (
                      forgotPassword.length >= 8 ? (
                        <p className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
                          <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
                          Password meets minimum length requirement (8+ chars)
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 font-medium mt-1 flex items-center gap-1">
                          Password must be at least 8 characters ({forgotPassword.length}/8)
                        </p>
                      )
                    ) : null}
                  </div>

                  {/* Confirm New Password */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Confirm Password <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400">
                        <Lock size={18} />
                      </span>
                      <input
                        type={showForgotConfirmPwd ? "text" : "password"}
                        required
                        value={forgotConfirmPassword}
                        onChange={(e) => setForgotConfirmPassword(e.target.value)}
                        className={getForgotPwdInputClass(forgotSubmittedStep3 && step3Errors.confirmPassword)}
                        placeholder="Confirm your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotConfirmPwd(!showForgotConfirmPwd)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        {showForgotConfirmPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {forgotSubmittedStep3 && step3Errors.confirmPassword ? (
                      <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                        {step3Errors.confirmPassword}
                      </p>
                    ) : forgotConfirmPassword ? (
                      forgotConfirmPassword === forgotPassword ? (
                        <p className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
                          <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
                          Passwords match!
                        </p>
                      ) : (
                        <p className="text-xs text-rose-500 font-medium mt-1 flex items-center gap-1">
                          <span className="inline-block w-3.5 h-3.5 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                          Passwords do not match.
                        </p>
                      )
                    ) : null}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setForgotStep(2);
                        setForgotError('');
                        setForgotSubmittedStep2(false);
                      }}
                      className="w-1/3 py-3 border border-gray-200 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-2/3 py-3 bg-[#1A734E] hover:bg-[#155D3F] text-white rounded-xl text-sm font-bold active:scale-[0.98] transition-all shadow-md shadow-[#1A734E]/20 flex items-center justify-center cursor-pointer disabled:opacity-50"
                    >
                      {forgotLoading ? "Resetting..." : "Reset Password"}
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 4: Success Screen */}
              {forgotStep === 4 && (
                <div className="text-center py-6 space-y-4">
                  <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto text-emerald-500 border border-emerald-100">
                    <CheckCircle2 size={36} />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Success!</h4>
                  <p className="text-sm text-gray-500">Your password has been successfully reset. Redirecting you to login...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
